/**
  Routes for Vivre Control Plane
  Each route includes `permissions` array for RBAC enforcement
*/

// Auth layouts
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";

// RBAC
import ProtectedRoute from "components/ProtectedRoute";
import { PERMISSIONS } from "config/roles";

// Control Plane Pages
import ControlPlaneHome from "Pages/controlPlane/Home";
import TenantList from "Pages/controlPlane/Tenants";
import TenantDetail from "Pages/controlPlane/Tenants/TenantDetail";
import Lifecycle from "Pages/controlPlane/Lifecycle";
import Configuration from "Pages/controlPlane/Configuration";
import CostBilling from "Pages/controlPlane/CostBilling";
import HealthStatus from "Pages/controlPlane/HealthStatus";
import Analytics from "Pages/controlPlane/Analytics";
import Subscriptions from "Pages/controlPlane/Subscriptions";
import Admin from "Pages/controlPlane/Admin";

// Legacy Pages
import UserConsole from "./Pages/userConsole/index";
import Maintenance from "./Pages/maintenance/index";
import UsersList from "Pages/userList";
import UserConfig from "Pages/userConfig";

// @mui icons
import Icon from "@mui/material/Icon";

const routes = [
  // ─── Control Plane ─────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Home",
    key: "home",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/home",
    permissions: [],
    component: (
      <ProtectedRoute>
        <ControlPlaneHome />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Tenants",
    key: "tenants",
    icon: <Icon fontSize="small">apartment</Icon>,
    route: "/tenants",
    permissions: [PERMISSIONS.TENANTS_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.TENANTS_READ]}>
        <TenantList />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Lifecycle",
    key: "lifecycle",
    icon: <Icon fontSize="small">sync_alt</Icon>,
    route: "/lifecycle",
    permissions: [PERMISSIONS.LIFECYCLE_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.LIFECYCLE_READ]}>
        <Lifecycle />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Configuration",
    key: "configuration",
    icon: <Icon fontSize="small">tune</Icon>,
    route: "/configuration",
    permissions: [PERMISSIONS.CONFIG_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_READ]}>
        <Configuration />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Cost & Billing",
    key: "cost",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/cost",
    permissions: [PERMISSIONS.COST_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.COST_READ]}>
        <CostBilling />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Health & Status",
    key: "health",
    icon: <Icon fontSize="small">monitor_heart</Icon>,
    route: "/health",
    permissions: [PERMISSIONS.HEALTH_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.HEALTH_READ]}>
        <HealthStatus />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Analytics",
    key: "analytics",
    icon: <Icon fontSize="small">analytics</Icon>,
    route: "/analytics",
    permissions: [PERMISSIONS.ANALYTICS_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.ANALYTICS_READ]}>
        <Analytics />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Subscriptions",
    key: "subscriptions",
    icon: <Icon fontSize="small">subscriptions</Icon>,
    route: "/subscriptions",
    permissions: [PERMISSIONS.SUBSCRIPTIONS_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.SUBSCRIPTIONS_READ]}>
        <Subscriptions />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Maintenance",
    key: "maintenanceSchedule",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/maintenanceSchedule",
    permissions: [PERMISSIONS.LIFECYCLE_WRITE],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.LIFECYCLE_WRITE]}>
        <Maintenance />
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Admin",
    key: "admin",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/admin",
    permissions: [PERMISSIONS.ADMIN_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.ADMIN_READ]}>
        <Admin />
      </ProtectedRoute>
    ),
  },

  // ─── Hidden Routes ─────────────────────────────────────────────────
  {
    type: "bind",
    key: "tenant-detail",
    route: "/tenants/:id",
    permissions: [PERMISSIONS.TENANTS_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.TENANTS_READ]}>
        <TenantDetail />
      </ProtectedRoute>
    ),
  },
  {
    type: "bind",
    key: "customerconsole",
    route: "/customerconsole",
    permissions: [PERMISSIONS.TENANTS_WRITE],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.TENANTS_WRITE]}>
        <UserConsole />
      </ProtectedRoute>
    ),
  },
  {
    type: "bind",
    key: "customerslist",
    route: "/customerslist",
    permissions: [PERMISSIONS.TENANTS_READ],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.TENANTS_READ]}>
        <UsersList />
      </ProtectedRoute>
    ),
  },
  {
    type: "bind",
    key: "userconfig",
    route: "/customerConfig",
    permissions: [PERMISSIONS.CONFIG_WRITE],
    component: (
      <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_WRITE]}>
        <UserConfig />
      </ProtectedRoute>
    ),
  },
  {
    type: "auth",
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "auth",
    key: "sign-up",
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
];

export default routes;
